import {useQuery, useMutation, useQueryClient} from 'react-query'
import {
  fetchMedicalEnquiryList,
  uploadMedicalEnquiryFiles,
  deleteMedicalEnquiryDocument,
  answerMedicalEnquiry,
} from '../services/medicalEnquiryService'
import {UploadFilesParams} from '../types/medicalEnquiry'

export const useMedicalEnquiry = (claimNumber: string) => {
  const {
    data: medicalEnquiries = [],
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery(['medicalEnquiry', claimNumber], () => fetchMedicalEnquiryList(claimNumber), {
    enabled: !!claimNumber,
    refetchOnWindowFocus: false,
    onError: (err) => {
      console.error('Error fetching medical enquiries:', err)
    },
  })

  return {
    medicalEnquiries,
    isLoading,
    error,
    refetch,
    isFetching,
  }
}

export const useUploadMedicalEnquiryFiles = () => {
  const queryClient = useQueryClient()

  return useMutation((params: UploadFilesParams) => uploadMedicalEnquiryFiles(params), {
    onSuccess: (data, variables) => {
      // Invalidate the medical enquiry query to refetch the updated data
      queryClient.invalidateQueries(['medicalEnquiry', variables.claimNumber])
    },
    onError: (error) => {
      console.error('Error uploading files:', error)
    },
  })
}

export const useDeleteMedicalEnquiryDocument = () => {
  const queryClient = useQueryClient()

  return useMutation(
    (params: {claimNumber: string; idDoc: number; username: string}) =>
      deleteMedicalEnquiryDocument(params.claimNumber, params.idDoc, params.username),
    {
      onSuccess: (data, variables) => {
        // Invalidate the medical enquiry query to refetch the updated data
        queryClient.invalidateQueries(['medicalEnquiry', variables.claimNumber])
      },
      onError: (error) => {
        console.error('Error deleting document:', error)
      },
    }
  )
}

export const useAnswerMedicalEnquiry = () => {
  const queryClient = useQueryClient()

  return useMutation(
    (params: {claimNumber: string; idMe: number; message: string; username: string}) =>
      answerMedicalEnquiry(params.claimNumber, params.idMe, params.message, params.username),
    {
      onSuccess: (data, variables) => {
        // Invalidate both medical enquiry and superseding queries
        queryClient.invalidateQueries(['medicalEnquiry', variables.claimNumber])
        queryClient.invalidateQueries(['supersedingCycles', variables.claimNumber])
        queryClient.invalidateQueries(['claims'])
      },
      onError: (error) => {
        console.error('Error answering medical enquiry:', error)
      },
    }
  )
}
